/**
 * Build a renderable tree for the Detailed / Side-by-side sheets from the
 * `_node` metadata ({kind, path, label}) that ReportCentreVenueService /
 * ReportCentreEventService attach to every on-screen row. The tree puts the
 * whole hierarchy (Revenue/Expenditure → Workforce/Non-Workforce → Department
 * → Cost Centre → Project → Template/Pack → Item) into the first column with
 * per-node expand/collapse, matching the Budgets by Department report.
 *
 * Returns null when no row carries metadata (snapshots stored before the tree
 * layout existed) so callers can fall back to the flat renderer.
 *
 * Output:
 * {
 *   blocks: [               // in sheet order
 *     { kind: 'section', label }                  // charcoal full-width header
 *     { kind: 'nodes', nodes: [Node, ...] }       // the section's hierarchy
 *     { kind: 'total', label, row, idx }          // Total Revenue / Net rows
 *   ],
 *   nodeKeys: [key, ...],   // every node key, for expand/collapse-all
 * }
 *
 * Node: {
 *   key,      // path joined — stable expansion-state key
 *   label,
 *   level,    // 0 for nodes directly under a section header
 *   values,   // numeric cells; from the backend subtotal row when one exists,
 *             // otherwise summed from descendant leaves (template/pack level)
 *   entries: [entry, ...],  // children in sheet order — leaves and sub-nodes
 *                           // interleave exactly like Budgets by Department
 *                           // (loose lines before cost-centre groups for
 *                           // expenditure, after them for revenue)
 * }
 *
 * entry: { type: 'leaf', row, idx, label }   // idx = original flat row index,
 *                                            // so review-comment keys stay stable
 *      | { type: 'node', node: Node }
 */

const sumNumericFields = (acc, row) => {
    Object.entries(row).forEach(([field, value]) => {
        if (typeof value === 'number') {
            acc[field] = (acc[field] || 0) + value;
        }
    });
};

export const buildDimensionReportTree = (rows) => {
    if (!Array.isArray(rows) || !rows.some((row) => row && !Array.isArray(row) && row._node)) {
        return null;
    }

    const blocks = [];
    const nodesByKey = new Map();
    let nodesBlock = null;

    const currentNodesBlock = () => {
        if (!nodesBlock) {
            nodesBlock = { kind: 'nodes', nodes: [] };
            blocks.push(nodesBlock);
        }
        return nodesBlock;
    };

    // Nodes are created on first reference — by their own subtotal row or by a
    // descendant — so entry order follows the sheet's row order. Intermediate
    // levels without a subtotal row (template/pack) stay synthesized: row=null.
    const ensureNode = (path) => {
        const key = path.join(' > ');
        if (nodesByKey.has(key)) {
            return nodesByKey.get(key);
        }

        const node = {
            key,
            label: path[path.length - 1],
            level: path.length - 2,
            row: null,
            entries: [],
        };
        nodesByKey.set(key, node);

        if (path.length > 2) {
            ensureNode(path.slice(0, -1)).entries.push({ type: 'node', node });
        } else {
            currentNodesBlock().nodes.push(node);
        }

        return node;
    };

    rows.forEach((row, idx) => {
        if (!row || Array.isArray(row) || !row._node) return;
        const { kind, path, label } = row._node;

        if (kind === 'section') {
            blocks.push({ kind: 'section', label });
            nodesBlock = null;
        } else if (kind === 'node') {
            const node = ensureNode(path);
            node.row = row;
            if (label) {
                node.label = label;
            }
        } else if (kind === 'leaf') {
            ensureNode(path).entries.push({ type: 'leaf', row, idx, label });
        } else if (kind === 'total') {
            blocks.push({ kind: 'total', label, row, idx });
            nodesBlock = null;
        }
        // 'skip' rows only exist for the flat fallback view
    });

    const computeValues = (node) => {
        node.entries.forEach((entry) => {
            if (entry.type === 'node') {
                computeValues(entry.node);
            }
        });

        const values = {};
        if (node.row) {
            sumNumericFields(values, node.row);
        } else {
            node.entries.forEach((entry) => {
                sumNumericFields(values, entry.type === 'node' ? entry.node.values : entry.row);
            });
        }
        node.values = values;
    };

    blocks.forEach((block) => {
        if (block.kind === 'nodes') {
            block.nodes.forEach(computeValues);
        }
    });

    return { blocks, nodeKeys: [...nodesByKey.keys()] };
};

export default buildDimensionReportTree;
